import { useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  Divider,
  Switch,
  Stack,
} from '@mui/material'
import {
  pagesUnlockedByPermissions,
  summarizePermissionsForHumans,
} from '../../utils/adminPermissions'

const togglePermission = (prev, subjectSchema, action, checked) => {
  const subject = subjectSchema.subject
  const allowed = new Set((subjectSchema.actions || []).map((a) => a.action))
  const next = { ...(prev || {}) }
  const subjectActions = { ...(next[subject] || {}) }

  if (checked) {
    subjectActions[action] = true
    // Edit implies View when the subject has a View action
    if (action === 'write' && allowed.has('read') && !subjectActions.read) {
      subjectActions.read = true
    }
  } else {
    delete subjectActions[action]
  }

  if (Object.keys(subjectActions).length === 0) {
    delete next[subject]
  } else {
    next[subject] = subjectActions
  }
  return next
}

/**
 * Non-technical permission editor: presets + grouped View/Edit toggles.
 */
export default function RolePermissionsEditor({
  permissionSchema = [],
  rolePresets = [],
  selectedPermissions,
  onChange,
  readOnly = false,
}) {
  const groups = useMemo(() => {
    const byGroup = new Map()
    const ungrouped = []
    for (const subjectSchema of permissionSchema) {
      if (!subjectSchema.group) {
        ungrouped.push(subjectSchema)
        continue
      }
      if (!byGroup.has(subjectSchema.group)) {
        byGroup.set(subjectSchema.group, [])
      }
      byGroup.get(subjectSchema.group).push(subjectSchema)
    }
    return { byGroup, ungrouped }
  }, [permissionSchema])

  const summaryLines = summarizePermissionsForHumans(
    permissionSchema,
    selectedPermissions,
  )
  const pageLabels = pagesUnlockedByPermissions(
    permissionSchema,
    selectedPermissions,
  )

  const applyPreset = (preset) => {
    if (readOnly) return
    onChange(structuredClone(preset.permissions || {}))
  }

  const handleToggle = (subjectSchema, action, checked) => {
    if (readOnly) return
    onChange(
      togglePermission(selectedPermissions, subjectSchema, action, checked),
    )
  }

  const renderSubject = (subjectSchema) => {
    const { subject, label, description, actions, portalPages } = subjectSchema
    return (
      <Box
        key={subject}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
        {(portalPages || []).length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
            {portalPages.map((page) => (
              <Chip
                key={page.path}
                label={page.label}
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            ))}
          </Stack>
        )}
        <Stack spacing={0.5}>
          {(actions || []).map((actionDef) => {
            const actionName = actionDef.action
            const checked = !!selectedPermissions?.[subject]?.[actionName]
            return (
              <FormControlLabel
                key={actionName}
                disabled={readOnly}
                control={
                  <Switch
                    size="small"
                    checked={checked}
                    onChange={(e) =>
                      handleToggle(subjectSchema, actionName, e.target.checked)
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {actionDef.label}
                    </Typography>
                    {actionDef.description && (
                      <Typography variant="caption" color="text.secondary">
                        {actionDef.description}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
              />
            )
          })}
        </Stack>
      </Box>
    )
  }

  return (
    <Box>
      {!readOnly && rolePresets.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Start from a recommended set
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pick a starter, then fine-tune below. You can combine areas as needed.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {rolePresets.map((preset) => (
              <Card
                key={preset.id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
                }}
              >
                <CardActionArea onClick={() => applyPreset(preset)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {preset.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {preset.outcome || preset.description}
                    </Typography>
                    {preset.warning && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                        {preset.warning}
                      </Alert>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          People with this role will be able to
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {summaryLines.map((line) => (
            <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5 }}>
              {line}
            </Typography>
          ))}
        </Box>
        {pageLabels.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Pages included
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {pageLabels.map((label) => (
                <Chip key={label} label={label} size="small" color="primary" variant="outlined" />
              ))}
            </Stack>
          </>
        )}
      </Box>

      {[...groups.byGroup.entries()].map(([groupName, subjects]) => (
        <Box key={groupName} sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.08, fontWeight: 700 }}
          >
            {groupName}
          </Typography>
          <Box sx={{ mt: 1 }}>{subjects.map(renderSubject)}</Box>
        </Box>
      ))}

      {groups.ungrouped.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.08, fontWeight: 700 }}
          >
            Other
          </Typography>
          <Box sx={{ mt: 1 }}>{groups.ungrouped.map(renderSubject)}</Box>
        </Box>
      )}
    </Box>
  )
}

/** Simple checkbox list used when embedding a single boolean without Switch density. */
export function RolePermissionCheckbox({
  label,
  description,
  checked,
  onChange,
  disabled,
}) {
  return (
    <FormControlLabel
      disabled={disabled}
      control={
        <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
      }
      label={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      }
    />
  )
}
